import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useStudio } from "@/context/StudioContext";
import { PageHeader, Panel, Loading, Tag, StatusBadge } from "@/components/common";
import { Images, Trash, HardDrives, FilmStrip, MusicNotes, Image as ImageIcon } from "@phosphor-icons/react";
import { toast } from "sonner";

const fmtBytes = (bytes) => {
  if (!bytes) return "0 B";
  if (bytes >= 1e9) return `${(bytes / 1e9).toFixed(1)} GB`;
  if (bytes >= 1e6) return `${(bytes / 1e6).toFixed(1)} MB`;
  return `${Math.max(1, bytes / 1e3).toFixed(0)} KB`;
};

const mediaGroup = (asset) => {
  const mime = asset.mime || "";
  if (mime.startsWith("video/")) return "video";
  if (mime.startsWith("audio/")) return "audio";
  if (mime.startsWith("image/")) return "image";
  return "other";
};

export default function AssetLibrary() {
  const { active } = useStudio();
  const [data, setData] = useState(null);
  const [group, setGroup] = useState("all");

  const load = () => api.get("/assets").then(({ data: result }) => setData(result));
  useEffect(() => { load(); }, [active?.id]);

  const remove = async (asset) => {
    if (!window.confirm("Remove this media item from the library?")) return;
    await api.delete(`/assets/${asset.id}`);
    toast.success("Removed from the library");
    load();
  };

  const counts = useMemo(() => {
    const result = { all: 0, video: 0, image: 0, audio: 0, other: 0 };
    (data?.assets || []).forEach((asset) => {
      result.all += 1;
      result[mediaGroup(asset)] += 1;
    });
    return result;
  }, [data]);

  if (!data) return <Loading />;
  const items = group === "all" ? data.assets : data.assets.filter((asset) => mediaGroup(asset) === group);

  return (
    <div>
      <PageHeader
        icon={Images}
        title="PinPilot Media Library"
        subtitle={`Real clips, final films, stills, music and effects for ${active.name}. Every item lives in object storage and can be reused in future productions.`}
        badge={<StatusBadge status="LIVE" />}
      />

      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Metric icon={FilmStrip} value={counts.video} label="Video clips & films" color="text-[#B88746]" />
        <Metric icon={ImageIcon} value={counts.image} label="Images & references" color="text-[#8CC5C0]" />
        <Metric icon={MusicNotes} value={counts.audio} label="Music & sound" color="text-[#D8E5DE]" />
        <Metric icon={HardDrives} value={fmtBytes(data.total_bytes)} label="Object storage" color="text-[#00FF9D]" />
      </div>

      <div className="mb-5 flex flex-wrap gap-2">
        {["all", "video", "image", "audio", "other"].map((id) => (
          <button key={id} onClick={() => setGroup(id)}
            className={`rounded-full border px-3 py-1.5 text-sm capitalize ${
              group === id ? "border-[#6FA9A5]/60 bg-[#276A6B]/15 text-[#8CC5C0]" : "border-white/10 text-zinc-500"
            }`}>
            {id} · {counts[id]}
          </button>
        ))}
      </div>

      {items.length === 0 ? (
        <Panel>
          <p className="text-sm text-zinc-500">
            No real {group === "all" ? "media" : group} stored yet. New PinPilot stills, animated clips and assembled films will appear here automatically.
          </p>
        </Panel>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {items.map((asset) => {
            const type = mediaGroup(asset);
            return (
              <Panel key={asset.id} className="flex flex-col overflow-hidden p-2">
                <div className="overflow-hidden rounded-xl border border-white/8 bg-black/50">
                  {type === "video" && (
                    <video src={asset.url} controls playsInline preload="metadata" className="aspect-video w-full object-contain" />
                  )}
                  {type === "image" && (
                    <img src={asset.url} alt={asset.title || asset.kind || "Studio asset"} loading="lazy" className="aspect-video w-full object-cover" />
                  )}
                  {type === "audio" && (
                    <div className="flex aspect-video flex-col items-center justify-center gap-4 p-5">
                      <MusicNotes size={42} weight="duotone" className="text-[#B88746]" />
                      <audio src={asset.url} controls preload="metadata" className="w-full" />
                    </div>
                  )}
                  {type === "other" && (
                    <div className="flex aspect-video items-center justify-center text-xs text-zinc-500">{asset.mime || "Stored file"}</div>
                  )}
                </div>

                <div className="flex-1 p-2">
                  <div className="truncate text-sm font-semibold text-white">
                    {asset.title || asset.filename || asset.kind?.replaceAll("_", " ") || asset.id}
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    <Tag className="text-[10px]">{type}</Tag>
                    <Tag className="text-[10px]">{asset.kind || "media"}</Tag>
                    {asset.capability && <Tag className="text-[10px]">{asset.capability}</Tag>}
                    {asset.provider && <Tag className="text-[10px]">{asset.provider}</Tag>}
                  </div>
                  <div className="mt-2 text-[10px] text-zinc-600">
                    {fmtBytes(asset.size)} · {asset.created_at ? new Date(asset.created_at).toLocaleString() : "stored"}
                  </div>
                </div>

                <div className="flex gap-2 p-2 pt-0">
                  <a href={asset.url} target="_blank" rel="noreferrer"
                    className="flex-1 rounded-full border border-white/10 px-3 py-1.5 text-center text-xs text-white">
                    Open
                  </a>
                  <button onClick={() => remove(asset)}
                    className="inline-flex items-center justify-center gap-1 rounded-full border border-[#FF3366]/35 px-3 py-1.5 text-xs text-[#FF6B7A]">
                    <Trash /> Remove
                  </button>
                </div>
              </Panel>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Metric({ icon: Icon, value, label, color }) {
  return (
    <Panel className="flex items-center gap-3 py-4">
      <Icon size={22} weight="duotone" className={color} />
      <div>
        <div className="font-display text-2xl font-black leading-none text-white">{value}</div>
        <div className="mt-1 text-[11px] text-zinc-500">{label}</div>
      </div>
    </Panel>
  );
}
